import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";

import DOMPurify from "dompurify";
import AOS from "aos";
import Swal from "sweetalert2";
import "aos/dist/aos.css";
import "../styles/contact.css";

const API_ENDPOINT =
  "https://nnr2y83koi.execute-api.us-east-1.amazonaws.com/prod/contact";

/* -------------------- validators -------------------- */

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email);

const isValidPhone = (phone) => /^[0-9+() \-]{6,30}$/.test(phone);

const isValidNotes = (notes) => notes.trim().length >= 10;

/* -------------------- component -------------------- */

const ContactForm = () => {
  const formRef = useRef();
  const [loading, setLoading] = useState(false);

  // controlled values
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");

  // validation states: null | "valid" | "invalid"
  const [emailState, setEmailState] = useState(null);
  const [phoneState, setPhoneState] = useState(null);
  const [notesState, setNotesState] = useState(null);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  /* -------------------- live validation -------------------- */

  useEffect(() => {
    if (!email) return setEmailState(null);
    setEmailState(isValidEmail(email) ? "valid" : "invalid");
  }, [email]);

  useEffect(() => {
    if (!phone) return setPhoneState(null);
    setPhoneState(isValidPhone(phone) ? "valid" : "invalid");
  }, [phone]);

  useEffect(() => {
    if (!notes) return setNotesState(null);
    setNotesState(isValidNotes(notes) ? "valid" : "invalid");
  }, [notes]);

  /* -------------------- submit -------------------- */

  const sendForm = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (
      emailState !== "valid" ||
      phoneState !== "valid" ||
      notesState !== "valid"
    ) {
      return;
    }

    setLoading(true);

    Swal.fire({
      title: "Küldés folyamatban…",
      text: "Kérjük, várjon egy pillanatig.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const payload = {
        name: DOMPurify.sanitize(formRef.current.name.value),
        email: DOMPurify.sanitize(email),
        phone: DOMPurify.sanitize(phone),
        notes: DOMPurify.sanitize(notes),
      };

      const res = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error();

      Swal.close();
      Swal.fire({
        icon: "success",
        title: "Sikeres elküldés",
        text: "Köszönjük! Üzenete megérkezett. Kollégánk 1 munkanapon belül felveszi Önnel a kapcsolatot.",
        confirmButtonText: "Rendben",
        confirmButtonColor: "#b9844a", // BRAND COLOR
        iconColor: "#b9844a",
        timer: 5000,
        timerProgressBar: true,
      });

      formRef.current.reset();
      setEmail("");
      setPhone("");
      setNotes("");
      setEmailState(null);
      setPhoneState(null);
      setNotesState(null);
    } catch {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Hiba történt",
        text: "Kérjük, próbálja meg később.",
        confirmButtonText: "Bezárás",
        confirmButtonColor: "#b42318",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="form-wrapper" id="contact">
      <div className="container">
        <div className="row">
          {/* LEFT: MAP + INFO */}
          <div className="col-md-6 mb-4 d-flex justify-content-center align-items-center">
            <div className="map-card">
              <iframe
                title="Rendelő térkép"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3337.1248055359983!2d20.19344587677421!3d47.17594791761628!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4741413fa9606da3%3A0x37c2ee725159675a!2sDr.%20Losonczi%20R%C3%A9ka!5e1!3m2!1shu!2shu!4v1752419235687!5m2!1shu!2shu"
                loading="lazy"
                allowFullScreen
              />
              <div className="contact-info">
                <h5>Elérhetőségek</h5>
                <p>5000 Szolnok, Sólyom utca 3/A 1/1.</p>
                <p>Fogszabályozás: +36 56 421 719</p>
                <p>Általános fogászat/implantáció: +36 56 220 851</p>
                <p>
                  <a href="mailto:info@fogorvosszolnok.hu">
                    info@fogorvosszolnok.hu
                  </a>
                </p>

                <div className="legal-links">
                  <Link to="/adatvedelmi-tajekoztato">
                    Adatvédelmi tájékoztató
                  </Link>
                  <span className="legal-separator"> | </span>
                  <Link to="/impresszum">Impresszum</Link>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: FORM */}
          <div className="col-md-6">
            <div className="form-container" data-aos="fade-up">
              <h2>Időpontfoglalás</h2>
              <div className="orange-divider" />
              <p className="description">
                Töltse ki az űrlapot, és rövid időn belül visszahívjuk.
              </p>

              <form ref={formRef} onSubmit={sendForm} noValidate>
                <input type="text" name="name" placeholder="Név *" required />

                <input
                  type="email"
                  name="email"
                  placeholder="E-mail cím *"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`live-${emailState}`}
                  required
                />
                {emailState === "invalid" && (
                  <div className="field-error-text">Érvénytelen e-mail cím</div>
                )}

                <input
                  type="tel"
                  name="phone"
                  placeholder="Telefonszám *"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={`live-${phoneState}`}
                  required
                />
                {phoneState === "invalid" && (
                  <div className="field-error-text">
                    Érvénytelen telefonszám
                  </div>
                )}

                <textarea
                  name="notes"
                  rows="5"
                  placeholder="Panasz / kérdés (min. 10 karakter)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className={`live-${notesState}`}
                />
                {notesState === "invalid" && (
                  <div className="field-error-text">
                    Legalább 10 karakter szükséges
                  </div>
                )}

                <button
                  className="contact-button"
                  type="submit"
                  disabled={loading}
                >
                  Küldés
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
